/**
 * LoginV2 — v2 reskin of auth/Login (UI-v2 Phase 1e).
 *
 * ALL auth logic/hooks/mutations reused verbatim from v1 Login.tsx.
 * Only the presentation layer changes: AuthShellV2 replaces AuthShell,
 * shadcn ui/input + ui/button replace common/Input + common/Button,
 * FormFieldV2 replaces FormField.
 *
 * NO passwordless/WebAuthn changes — that is a separate deferred card.
 * MfaVerifyModal is kept as-is (logic, not presentation).
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useTenantBase } from "@/hooks/useTenantBase";
import { useLogin } from "@/hooks/queries/auth/useLogin";
import { isMfaPendingResponse } from "@/lib/loginResponse";
import { MfaVerifyModal } from "@/components/auth/MfaVerifyModal";
import { useAuthStore } from "@/stores/useAuthStore";
import { AuthShellV2 } from "@/components/v2/layout/AuthShellV2";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormFieldV2 } from "@/components/v2/patterns/FormV2";

export default function LoginV2(): JSX.Element {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const base = useTenantBase();
  const navigate = useNavigate();
  const login = useLogin();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [fullPageError, setFullPageError] = useState<string | null>(null);
  const [showMfaModal, setShowMfaModal] = useState(false);

  // HARD-9: wipe stale mfa_pending on mount
  useEffect(() => {
    useAuthStore.getState().mfaPendingClear();
  }, []);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFieldError(null);
    setFullPageError(null);

    if (!tenantSlug) return;

    login.mutate(
      { tenantSlug, email, password },
      {
        onSuccess: (data) => {
          if (isMfaPendingResponse(data)) {
            setShowMfaModal(true);
            return;
          }
          // AU1: stay in the v2 tree after a v2 login (was /:slug/dashboard).
          navigate(`${base}/dashboard`, { replace: true });
        },
        onError: (err) => {
          switch (err.error) {
            case "invalid_credentials":
              setFieldError("Email or password incorrect.");
              break;
            case "too_many_attempts": {
              const retryAfter = typeof err.details["retry_after_seconds"] === "number"
                ? err.details["retry_after_seconds"]
                : null;
              const minutes = retryAfter ? Math.ceil(retryAfter / 60) : null;
              setFieldError(
                `Account temporarily locked. ${minutes ? `Try again in ${minutes} minute${minutes === 1 ? "" : "s"}.` : "Try again later."}`
              );
              break;
            }
            case "tenant_suspended":
              setFullPageError("This workspace is suspended. Contact support.");
              break;
            case "tenant_pending_deletion":
              setFullPageError("This workspace is pending deletion.");
              break;
            case "validation_error":
              setFieldError("Check the email and password fields.");
              break;
            default:
              setFieldError("That didn't go through. Try again.");
          }
        },
      }
    );
  };

  if (fullPageError) {
    return (
      <AuthShellV2 title="Workspace unavailable">
        <p className="text-sm text-text-secondary">{fullPageError}</p>
        <a
          href="mailto:support@certainhr.ai"
          className="mt-4 inline-block text-sm text-text-link underline hover:text-text-link-hover"
        >
          Contact support
        </a>
      </AuthShellV2>
    );
  }

  return (
    <AuthShellV2
      title="Sign in"
      subtitle={tenantSlug ? `Workspace: ${tenantSlug}` : "Use your work email and password."}
      footer={
        <>
          <Link
            to={`${base}/forgot-password`}
            className="text-text-link hover:text-text-link-hover"
          >
            Forgot your password?
          </Link>
          <span className="mx-2 text-text-tertiary">·</span>
          <Link
            to={`${base}/signup`}
            className="text-text-link hover:text-text-link-hover"
          >
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <FormFieldV2 label="Email" htmlFor="email-v2">
          <Input
            id="email-v2"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            aria-invalid={!!fieldError || undefined}
          />
        </FormFieldV2>
        <FormFieldV2
          label="Password"
          htmlFor="password-v2"
          error={fieldError ?? undefined}
        >
          <Input
            id="password-v2"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            aria-invalid={!!fieldError || undefined}
            className={fieldError ? "border-verdict-gap-fg focus-visible:border-verdict-gap-fg" : ""}
          />
        </FormFieldV2>
        <Button
          type="submit"
          className="w-full"
          disabled={login.isPending}
          aria-busy={login.isPending}
        >
          {login.isPending ? "Signing in…" : "Sign in"}
        </Button>
      </form>
      {showMfaModal && tenantSlug ? (
        <MfaVerifyModal
          variant="tenant"
          tenantSlug={tenantSlug}
          onComplete={() =>
            // AU1: MFA success also stays in the v2 tree.
            navigate(`${base}/dashboard`, { replace: true })
          }
          onClose={() => {
            useAuthStore.getState().mfaPendingClear();
            setShowMfaModal(false);
          }}
        />
      ) : null}
    </AuthShellV2>
  );
}
